import { lightTheme } from '@/customTheme'
import { PLACEMENT, StatefulPopover } from 'baseui/popover'
import { useTranslation } from 'react-i18next'
import { StyleObject, useStyletron } from 'styletron-react'
import PromptInputShell from '@/components/PromptInputShell'
import PHOTO_0 from '@/assets/images/thumbnail-style/photo0.png'
import PHOTO_1 from '@/assets/images/thumbnail-style/photo1.png'
import PHOTO_2 from '@/assets/images/thumbnail-style/photo2.png'
import PHOTO_3 from '@/assets/images/thumbnail-style/photo3.png'
import PHOTO_4 from '@/assets/images/thumbnail-style/photo4.png'
import RATIO_1 from '@/assets/images/ratio/ratio1.svg'
import SMALL_RATIO_1 from '@/assets/images/ratio/small-ratio-1.svg'
import RATIO_2 from '@/assets/images/ratio/ratio2.svg'
import SMALL_RATIO_2 from '@/assets/images/ratio/small-ratio-2.svg'
import RATIO_3 from '@/assets/images/ratio/ratio3.svg'
import SMALL_RATIO_3 from '@/assets/images/ratio/small-ratio-3.svg'
import Icons from '../../../Icons'
import { useEffect, useState, useCallback, useMemo, useRef, type MouseEvent } from 'react'
import NetworkStatusService from '@/shared/services/NetworkStatusService'
import DynamicTextarea from './DynamicTextarea'
import { styled } from 'baseui'
import { nsfwManager } from '@/utils/nsfwManager'
import { ObjectType } from '@/scenes/engine/common/constants'
import { showTextModal } from '@/components/ShowModal/textModalConfigs'
import { useSelector } from 'react-redux'
import { selectUserIsPremium } from '@/store/slices/user/selectors'
import { useAppDispatch } from '@/store/store'
import { setOpenModalTryPremium } from '@/store/slices/user/actions'
import VideoIntroAIImage from '@assets/video/VideoIntroAIImage.mp4'
import VideoIntroAIImagePlaceHolder from '@assets/images/VideoIntroAIImage_frame_0.jpg'
import { customAmplitude } from '@/utils/customAmplitude'
import ImageProcessor from '../../../FloatingImprover/ImageProcessor'
import { StaticImage } from 'fabric/fabric-impl'
import { fabric } from 'fabric'
import Tooltip from '@/components/Tooltip'
import ImagePreview from './ImagePreview'
import { Z_INDEX } from '@/constants/z-index'
import { hexToRgba } from '@/scenes/engine/utils/utils'

// -------------------- Types & Interfaces --------------------
export interface ActionsConfig {
  showRatio?: boolean;
  showStyle?: boolean;
  showSuggestEdit?: boolean;
  suggestEditHasQuickActions?: boolean;
  showPreviewImage?: boolean;
}

// -------------------- Constants & Styled (hoisted) --------------------
const STYLES = [
  {
    icon: PHOTO_0,
    text: 'Style',
    id: 0
  },
  {
    icon: PHOTO_1,
    text: 'Photo',
    id: 1
  },
  {
    icon: PHOTO_2,
    text: 'Digital Art',
    id: 2
  },
  {
    icon: PHOTO_3,
    text: 'Watercolor',
    id: 3
  },
  {
    icon: PHOTO_4,
    text: '3D',
    id: 4
  },
]

const RATIOS: RatioItem[] = [
  {
    icon: RATIO_1,
    smallIcon: SMALL_RATIO_1,
    text: '1:1',
    size: {
      width: 1024,
      height: 1024
    },
    id: 0
  },
  {
    icon: RATIO_2,
    smallIcon: SMALL_RATIO_2,
    text: '16:9',
    size: {
      width: 1024,
      height: 576
    },
    id: 1
  },
  {
    icon: RATIO_3,
    smallIcon: SMALL_RATIO_3,
    text: '9:16',
    size: {
      width: 576,
      height: 1024
    },
    id: 2
  },
]

const enum SUB_MENUS {
  SUGGEST_EDIT = 'suggest-edit',
  STYLE = 'style',
  RATIO = 'ratio',
} 

// Styled components hoisted outside to avoid recreation on each render
const ActionButtonsContainer = styled('div', {
  display: 'flex',
  gap: '8px',
  width: '100%',
  alignItems: 'center',
  paddingRight: '12px'
});

const OptionButton = styled('div', (props: {style: StyleObject, $isActive?: boolean}) => ({
  height: '32px',
  padding: '4px 12px 4px 8px',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  background: props.$isActive ? lightTheme.colors.grayScale100 : lightTheme.colors.grayScale50,
  borderRadius: '100px',
  cursor: 'pointer',
  flexShrink: 0,
  flexBasis: 'auto',
  ':hover': {
    background: lightTheme.colors.grayScale100,
  },
  ':active': {
    background: lightTheme.colors.grayScale100,
  },
  ...props.style
}));

const IconImage = styled('img', {
  width: '24px',
  height: '24px',
  flexShrink: 0,
  objectFit: 'contain',
});

const OptionText = styled('span', (props: { $disabled?: boolean }) => ({
  ...lightTheme.typography.Small12medium,
  color: props.$disabled ? lightTheme.colors.grayScale200 : lightTheme.colors.grayScale600,
  maxWidth: '148px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const SendButton = styled('button', (props: { $disabled?: boolean; $isActive?: boolean; $isLoading?: boolean }) => ({
  position: 'relative',
  width: '32px',
  height: '32px',
  border: 'none',
  outline: 'none',
  background: props.$disabled ? lightTheme.colors.grayScale50 : props.$isLoading ? lightTheme.colors.black : lightTheme.colors.primary,
  borderRadius: '50%',
  marginLeft: 'auto',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  pointerEvents: props.$disabled ? 'none' : 'auto',
  cursor: 'pointer',
  ':hover': {
    background: props.$isLoading ? lightTheme.colors.grayScale800 : 'rgba(229, 5, 86, 1)'
  },
  ':active': {
    background: props.$isLoading ? lightTheme.colors.grayScale800 : 'rgba(204, 4, 77, 1)'
  },
  filter: props.$disabled || props.$isLoading ? 'none' : 'drop-shadow(0px 0px 12px rgba(255, 5, 96, 0.25))'
}));

// Reusable menu list used by Style and Ratio popovers
type BaseMenuItem = { id: number; icon: string; text: string };
const MenuList = <T extends BaseMenuItem>({
  titleKey,
  items,
  selectedId,
  onSelect,
  onCancelInteraction,
  skipFirst = false,
  height = 'auto',
}: {
  titleKey: string;
  items: T[];
  selectedId: number;
  onSelect: (item: T) => void;
  onCancelInteraction?: () => void;
  skipFirst?: boolean;
  height?: string;
}) => {
  const { t } = useTranslation();
  const [css] = useStyletron();
  const dragStartPointRef = useRef<{ x: number; y: number } | null>(null);

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!dragStartPointRef.current || e.buttons !== 1) return;

    const deltaX = Math.abs(e.clientX - dragStartPointRef.current.x);
    const deltaY = Math.abs(e.clientY - dragStartPointRef.current.y);
    const hasStartedDragging = deltaX > 4 || deltaY > 4;

    if (hasStartedDragging) {
      dragStartPointRef.current = null;
      onCancelInteraction?.();
    }
  };

  return (
    <div
      style={{ width: '292px', height, background: lightTheme.colors.white, borderRadius: '16px', padding: '16px' }}
    >
      <h5
        style={{ ...lightTheme.typography.Small16Bold, fontWeight: 600, paddingLeft: '8px' }}
        onPointerDownCapture={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClickCapture={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {t(titleKey)}
      </h5>
      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {items.map((item, index) => (!skipFirst || index !== 0) && (
          <div
            key={item.id}
            className={css({
              height: '70px',
              padding: '8px 16px 8px 8px',
              background: lightTheme.colors.white,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'start',
              gap: '16px',
              ':hover': { background: lightTheme.colors.grayScale50 },
              borderRadius: '8px',
              cursor: 'pointer',
              userSelect: 'none',
              WebkitUserDrag: 'none',
            })}
            onMouseDown={e => {
              e.stopPropagation();
              dragStartPointRef.current = { x: e.clientX, y: e.clientY };
            }}
            onMouseUp={() => {
              dragStartPointRef.current = null;
            }}
            onMouseMove={handleMouseMove}
            onDragStart={e => {
              e.preventDefault();
              e.stopPropagation();
              dragStartPointRef.current = null;
              onCancelInteraction?.();
            }}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              dragStartPointRef.current = null;
              onSelect(item);
            }}
          >
            <img src={item.icon} alt="icon" style={{ width: '54px', height: '54px', borderRadius: '4px' }} draggable={false} />
            <p style={{ ...lightTheme.typography.Small14regular, color: lightTheme.colors.grayScale600 }}>{t(item.text)}</p>
            {item.id === selectedId ? (<div style={{ marginLeft: 'auto' }}><Icons.CheckIconSmall fill={'#FF0560'} /></div>) : null}
          </div>
        ))}
      </div>
    </div>
  );
};

interface RatioItem {
  id: number
  icon: string
  smallIcon: string
  text: string
  size: {
    width: number
    height: number
  }
}

interface QueryValue {
  selectedStyle: typeof STYLES[number];
  selectedRatio: RatioItem;
  prompt: string;
}

// -------------------- Component Interfaces --------------------
interface StyleSelectorProps {
  selectedStyle: typeof STYLES[number];
  setSelectedStyle: (style: typeof STYLES[number]) => void;
  subMenuIsOpen: SUB_MENUS | null;
  setSubMenuIsOpen: (menu: SUB_MENUS | null) => void;
  disabled: boolean;
}

interface RatioSelectorProps {
  selectedRatio: RatioItem;
  setSelectedRatio: (ratio: RatioItem) => void;
  subMenuIsOpen: SUB_MENUS | null;
  setSubMenuIsOpen: (menu: SUB_MENUS | null) => void;
  disabled: boolean;
}

interface SuggestEditButtonProps {
  subMenuIsOpen: SUB_MENUS | null;
  setSubMenuIsOpen: (menu: SUB_MENUS | null) => void;
  disabled: boolean;
  isLoadingSuggestImages: boolean;
  activeObject: StaticImage | fabric.ActiveSelection | null;
  onStartLoading?: () => void;
  onStopLoading?: () => void;
  onSuccess?: () => void;
  suggestEditHasQuickActions?: boolean;
}

interface SubmitButtonProps {
  isLoadingSuggestImages: boolean;
  isDefaultValue?: boolean;
  prompt: string;
  hasPremium: boolean;
  showPremiumBadge?: boolean;
  hasConnection: boolean;
  handleSubmit: () => void;
  handleCancelSubmit: () => void;
}

interface ActionButtonsProps {
  actionsConfig: ActionsConfig;
  selectedStyle: typeof STYLES[number];
  setSelectedStyle: (style: typeof STYLES[number]) => void;
  selectedRatio: RatioItem;
  setSelectedRatio: (ratio: RatioItem) => void;
  subMenuIsOpen: SUB_MENUS | null;
  setSubMenuIsOpen: (menu: SUB_MENUS | null) => void;
  disabled: boolean;
  isLoadingSuggestImages: boolean;
  isDefaultValue?: boolean;
  prompt: string;
  hasPremium: boolean;
  showPremiumBadge?: boolean;
  hasConnection: boolean;
  handleSubmit: () => void;
  handleCancelSubmit: () => void;
  activeObject?: StaticImage | fabric.ActiveSelection | null;
  editor?: any;
  currentSceneId?: string;
  onStartLoading?: () => void;
  onStopLoading?: () => void;
  onSuccess?: () => void;
}

interface TextBoxWrapperProps {
  wrapperRef: React.RefObject<HTMLDivElement>;
  isCollapsed: boolean;
  actionsConfig: ActionsConfig;
  disabled: boolean;
  children: React.ReactNode;
}

interface TextBoxProps {
  prompt: string;
  setPrompt: (value: string) => void
  queryValue?: QueryValue;
  setQueryValue?: (value: QueryValue) => void;
  isLoadingSuggestImages: boolean
  isDefaultValue?: boolean
  setIsDefaultValue?: (value: boolean) => void;
  wrapScrollRef?: React.RefObject<HTMLDivElement>;
  isCollapsed?: boolean;
  setIsCollapsed?: (value: boolean) => void;
  actionsConfig?: ActionsConfig;
  mainStyle?: 'oneline' | 'actionContols';
  placeholder: string | string[];
  onSubmit?: () => void; // Optional callback for submit action
  activeObject?:  StaticImage | fabric.ActiveSelection | null; // Optional prop to pass active object
  editor?: any; // Editor instance to get page preview
  currentSceneId?: string; // Current scene ID
  onStartLoading?: () => void
  onStopLoading?: () => void
  onSuccess?: () => void
  disabled?: boolean
  autoFocus?: boolean
  onFocus?: () => void
  onBlur?: () => void
  onUndoRedoShortcut?: () => void
  onCancelSubmit?: () => void
  showClearTextButton?: boolean
  hasGradientBorder?: boolean // Enable gradient border styling for oneline mode
  /**
   * oneline mode on a narrow box: the text uses the row up to the send button,
   * whose room the box already keeps, instead of reserving that room again.
   */
  compact?: boolean
  textareaId?: string // ID for the textarea element
  premiumVideo?: string
  premiumPlaceholder?: string
  premiumModalSource?: string
  showPremiumBadge?: boolean
  source?: string
  /** Optional URL thumbnail shown left of the textarea (AIStudio mode). When provided and showPreviewImage is true, renders this URL instead of the active-object ImagePreview. */
  thumbnailSrc?: string | null
  showErrorState?: boolean
}

// -------------------- Separated Components --------------------

// TextBoxWrapper Component
function TextBoxWrapper({
  wrapperRef,
  isCollapsed,
  actionsConfig,
  disabled,
  children
}: TextBoxWrapperProps) {
  const [css] = useStyletron();
  
  return (
    <div
      ref={wrapperRef}
      id="wrapper-textarea"
      className={css({
        position: 'relative',
        boxSizing: 'border-box',
        background: lightTheme.colors.white,
        padding: isCollapsed
          ? '12px'
          : '16px 4px 16px 16px',
        marginBottom: 0,
        height: isCollapsed ? '48px' : 'auto',
        width: '100%',
        pointerEvents: disabled ? 'none' : 'auto',
        border: `1px solid ${lightTheme.colors.grayScale100}`,
        borderRadius: isCollapsed ? '8px' : '16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
        gap: '16px',
        ':hover': {
          borderColor: disabled
            ? lightTheme.colors.grayScale100
            : lightTheme.colors.grayScale300,
        },
        ':focus-within': {
          borderColor: disabled
            ? lightTheme.colors.grayScale100
            : lightTheme.colors.grayScale300,
        },
        cursor: isCollapsed ? 'pointer' : 'auto',
      })}
    >
      {children}
    </div>
  );
}

// TextBoxWrapperOneLine Component - based on design reference
function TextBoxWrapperOneLine({
  wrapperRef,
  disabled,
  hasGradientBorder,
  isActive,
  hasThumbnail,
  showErrorState,
  children
}: Omit<TextBoxWrapperProps, 'isCollapsed'> & { hasGradientBorder?: boolean; isActive?: boolean, hasThumbnail?: boolean, showErrorState?: boolean }) {
  return (
    <PromptInputShell
      ref={wrapperRef}
      id="wrapper-textarea"
      disabled={disabled}
      active={isActive}
      hasGradientBorder={hasGradientBorder}
      hasThumbnail={hasThumbnail}
      showErrorState={showErrorState}
    >
      {children}
    </PromptInputShell>
  );
}

// StyleSelector Component
function StyleSelector({ 
  selectedStyle, 
  setSelectedStyle, 
  subMenuIsOpen, 
  setSubMenuIsOpen, 
  disabled 
}: StyleSelectorProps) {
  return (
    <StatefulPopover
      placement={PLACEMENT.bottom}
      popoverMargin={4}
      overrides={{
        Body: {
          style: ({ $theme }) => ({
            zIndex: '10000',
          }),
        },
      }}
      content={({ close }) => (
        <MenuList
          titleKey={'Choose style'}
          items={STYLES}
          selectedId={selectedStyle.id}
          onCancelInteraction={() => {
            setSubMenuIsOpen(null)
            close()
          }}
          skipFirst
          height={'356px'}
          onSelect={item => {
            setSelectedStyle(item.id === selectedStyle.id ? STYLES[0] : item)
            setSubMenuIsOpen(null)
            close()
          }}
        />
      )}
      triggerType="click"
      onOpen={() => setSubMenuIsOpen(SUB_MENUS.STYLE)}
      onClose={() => setSubMenuIsOpen(null)}
    >
      <OptionButton
        style={{
          paddingLeft: selectedStyle.id === 0 ? '8px' : '4px',
          background: subMenuIsOpen === SUB_MENUS.STYLE ? lightTheme.colors.grayScale100 : lightTheme.colors.grayScale50,
          pointerEvents: subMenuIsOpen === SUB_MENUS.STYLE || disabled ? 'none' : 'auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <IconImage
            src={selectedStyle.icon}
            alt="icon"
            style={{
              borderRadius: '50%',
            }}
            draggable={false}
          />
          <OptionText $disabled={disabled}>{selectedStyle.text}</OptionText>
        </div>
      </OptionButton>
    </StatefulPopover>
  )
}

// RatioSelector Component
function RatioSelector({ 
  selectedRatio, 
  setSelectedRatio, 
  subMenuIsOpen, 
  setSubMenuIsOpen, 
  disabled 
}: RatioSelectorProps) {
  return (
    <StatefulPopover
      placement={PLACEMENT.bottom}
      popoverMargin={4}
      overrides={{
        Body: {
          style: ({ $theme }) => ({
            zIndex: '10000',
          }),
        },
      }}
      content={({ close }) => (
        <MenuList
          titleKey={'Choose ratio'}
          items={RATIOS}
          selectedId={selectedRatio.id}
          onCancelInteraction={() => {
            setSubMenuIsOpen(null)
            close()
          }}
          height={'282px'}
          onSelect={item => {
            setSelectedRatio(item)
            setSubMenuIsOpen(null)
            close()
          }}
        />
      )}
      triggerType="click"
      onOpen={() => setSubMenuIsOpen(SUB_MENUS.RATIO)}
      onClose={() => setSubMenuIsOpen(null)}
    >
      <OptionButton
        style={{
          background: subMenuIsOpen === SUB_MENUS.RATIO ? lightTheme.colors.grayScale100 : lightTheme.colors.grayScale50,
          pointerEvents: subMenuIsOpen === SUB_MENUS.RATIO || disabled ? 'none' : 'auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <IconImage
            src={selectedRatio.smallIcon}
            alt="icon"
            draggable={false}
          />
          <OptionText $disabled={disabled}>{selectedRatio.text}</OptionText>
        </div>
      </OptionButton>
    </StatefulPopover>
  )
}

// SuggestEditButton Component
function SuggestEditButton({ 
  subMenuIsOpen, 
  setSubMenuIsOpen, 
  disabled, 
  isLoadingSuggestImages,
  activeObject,
  onStartLoading,
  onStopLoading,
  onSuccess,
  suggestEditHasQuickActions
}: SuggestEditButtonProps) {
  const { t } = useTranslation();
  
  const suggestEditContent = useMemo(() => (close: () => void) => (
    <ImageProcessor
      style={{ display: 'block' }}
      hasInput={false}
      hasQuickActions={suggestEditHasQuickActions}
      title="Suggested"
      activeObject={activeObject?.type === ObjectType.BAZAART_IMAGE ? activeObject as StaticImage : null}
      handleClose={close}
      onStartLoading={onStartLoading}
      onStopLoading={onStopLoading}
      onSuccess={() => {
        onSuccess?.()
        close()
      }}
    />
  ), [activeObject, onStartLoading, onStopLoading, onSuccess, suggestEditHasQuickActions]);

  return (
    <StatefulPopover
      placement={PLACEMENT.top}
      popoverMargin={8}
      overrides={{
        Body: {
          style: ({ $theme }) => ({
            zIndex: Z_INDEX.IMAGE_PROCESSOR,
          }),
        },
      }}
      content={({ close }) => suggestEditContent(() => {
        setSubMenuIsOpen(null)
        close()
      })}
      triggerType="click"
      onOpen={() => {
        setSubMenuIsOpen(SUB_MENUS.SUGGEST_EDIT); 
        customAmplitude('AI Edit - Suggested edits')
        customAmplitude('Click', { Source: 'Suggested edits'})
      }}
      onClose={() => setSubMenuIsOpen(null)}
      autoFocus={false}
    >
      <OptionButton
        $isActive={subMenuIsOpen === SUB_MENUS.SUGGEST_EDIT}
        style={{
          pointerEvents: subMenuIsOpen === SUB_MENUS.SUGGEST_EDIT || disabled || isLoadingSuggestImages ? 'none' : 'auto', 
          marginLeft: 'auto'
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <Icons.MagicStarsSmall
            fill={disabled || isLoadingSuggestImages ? lightTheme.colors.grayScale200 : lightTheme.colors.blackGray}
          />
          <OptionText $disabled={disabled || isLoadingSuggestImages} style={{ color: disabled || isLoadingSuggestImages ? lightTheme.colors.grayScale200 : lightTheme.colors.blackGray }}>
            {t('Suggest edits')}
          </OptionText>
        </div>
      </OptionButton>
    </StatefulPopover>
  )
}

// SubmitButton Component
function SubmitButton({
  isLoadingSuggestImages,
  isDefaultValue,
  prompt,
  hasPremium,
  showPremiumBadge = true,
  hasConnection,
  handleSubmit,
  handleCancelSubmit
}: SubmitButtonProps) {
  const isPromptEmpty = prompt.trim() === ''

  if (isLoadingSuggestImages) {
    return (
      <Tooltip
        info={{
          content: 'Stop',
          placement: 'top',
        }}
      >
        <SendButton
          $disabled={!hasConnection}
          onClick={handleCancelSubmit}
          $isLoading={true}
        >
          <div
            style={{
              background: lightTheme.colors.white,
              width: '10px',
              height: '10px',
              borderRadius: '2px',
            }}
          ></div>
        </SendButton>
      </Tooltip>
    )
  }

  return (
    <SendButton
      $disabled={isDefaultValue || isPromptEmpty || !hasConnection}
      onClick={handleSubmit}
    >
      <Icons.FullArrowRightIcon
        fill={isDefaultValue || isPromptEmpty ? 'rgba(204, 204, 204, 1)' : '#FFF'}
      />
      {!hasPremium && showPremiumBadge ? (
        <div style={{ position: 'absolute', top: '-4px', right: '-5px' }}>
          <Icons.PremiumIcon disabled={isDefaultValue || isLoadingSuggestImages || isPromptEmpty} />
        </div>
      ) : null}
    </SendButton>
  )
}

// ActionButtons Component
function ActionButtons({
  actionsConfig,
  selectedStyle,
  setSelectedStyle,
  selectedRatio,
  setSelectedRatio,
  subMenuIsOpen,
  setSubMenuIsOpen,
  disabled,
  isLoadingSuggestImages,
  isDefaultValue,
  prompt,
  hasPremium,
  showPremiumBadge,
  hasConnection,
  handleSubmit,
  handleCancelSubmit,
  activeObject,
  editor,
  currentSceneId,
  onStartLoading,
  onStopLoading,
  onSuccess
}: ActionButtonsProps) {

  return (
    <ActionButtonsContainer>
      {(actionsConfig.showSuggestEdit || actionsConfig.showPreviewImage) && (
        <div
          style={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flex: 1,
            height: '44px',
          }}
        >
          {actionsConfig.showPreviewImage && (
            <ImagePreview
              activeObject={activeObject}
              showCloseOnHover={false}
              onCloseClick={() => {}}
              editor={editor}
              currentSceneId={currentSceneId}
              disabled={disabled}
            />
          )}
          {actionsConfig.showSuggestEdit && (
            <SuggestEditButton
              subMenuIsOpen={subMenuIsOpen}
              setSubMenuIsOpen={setSubMenuIsOpen}
              disabled={disabled}
              isLoadingSuggestImages={isLoadingSuggestImages}
              activeObject={activeObject}
              onStartLoading={onStartLoading}
              onStopLoading={onStopLoading}
              onSuccess={onSuccess}
              suggestEditHasQuickActions={actionsConfig.suggestEditHasQuickActions}
            />
          )}
        </div>
      )}
      {actionsConfig.showStyle !== false && (
        <StyleSelector
          selectedStyle={selectedStyle}
          setSelectedStyle={setSelectedStyle}
          subMenuIsOpen={subMenuIsOpen}
          setSubMenuIsOpen={setSubMenuIsOpen}
          disabled={disabled}
        />
      )}
      {actionsConfig.showRatio !== false && (
        <RatioSelector
          selectedRatio={selectedRatio}
          setSelectedRatio={setSelectedRatio}
          subMenuIsOpen={subMenuIsOpen}
          setSubMenuIsOpen={setSubMenuIsOpen}
          disabled={disabled}
        />
      )}
      <SubmitButton
        isLoadingSuggestImages={isLoadingSuggestImages}
        isDefaultValue={isDefaultValue}
        prompt={prompt}
        hasPremium={hasPremium}
        showPremiumBadge={showPremiumBadge}
        hasConnection={hasConnection}
        handleSubmit={handleSubmit}
        handleCancelSubmit={handleCancelSubmit}
      />
    </ActionButtonsContainer>
  )
}

export default function TextBox({
  prompt,
  setPrompt,
  queryValue,
  setQueryValue,
  isLoadingSuggestImages,
  isDefaultValue,
  setIsDefaultValue,
  wrapScrollRef,
  isCollapsed,
  setIsCollapsed,
  actionsConfig = {},
  mainStyle = 'actionContols',
  placeholder,
  onSubmit,
  activeObject,
  editor,
  currentSceneId,
  onStartLoading,
  onStopLoading,
  onSuccess,
  disabled = false,
  autoFocus = true,
  onFocus,
  onBlur,
  onUndoRedoShortcut,
  onCancelSubmit,
  showClearTextButton,
  hasGradientBorder = false,
  compact = false,
  showErrorState,
  textareaId,
  premiumVideo,
  premiumPlaceholder,
  premiumModalSource,
  showPremiumBadge = true,
  source = '',
  thumbnailSrc,
}: TextBoxProps) {
  const { t } = useTranslation()
  const [css] = useStyletron()
  const [selectedStyle, setSelectedStyle] = useState(STYLES[0])
  const [selectedRatio, setSelectedRatio] = useState<RatioItem>(RATIOS[0])
  const [subMenuIsOpen, setSubMenuIsOpen] = useState<SUB_MENUS>(null);

  const hasPremium = useSelector(selectUserIsPremium)
  const dispatch = useAppDispatch()

  const [hasConnection, setHasConnection] = useState(() => NetworkStatusService.getInstance().getStatus());

  const wrapperRef = useRef<HTMLDivElement | null>(null)
  
  useEffect(() => {
      const service = NetworkStatusService.getInstance();
      const handleChange = (online: boolean) => setHasConnection(online);
      service.addListener(handleChange);
      return () => { service.removeListener(handleChange); };
  }, [])

  useEffect(() => {
    if(!setIsDefaultValue) return
    if (queryValue) {
      const sameStyle = queryValue.selectedStyle.text === selectedStyle.text
      const sameRatio = queryValue.selectedRatio.id === selectedRatio.id
      const samePrompt = queryValue.prompt === prompt
      setIsDefaultValue(sameStyle && sameRatio && samePrompt)
    } else {
      setIsDefaultValue(selectedStyle.id === 0 && selectedRatio.id === 0 && prompt === '')
    }
  }, [selectedStyle, selectedRatio, prompt, queryValue, setIsDefaultValue])

  const confirmQueryValue = useCallback((): boolean => {
    if(nsfwManager.isSafeText(prompt, source)) {
      setQueryValue?.({
        selectedStyle,
        selectedRatio,
        prompt
      })
      return true
    } else {
      showTextModal('NSFW_ERROR')
      return false
    }
  }, [prompt, selectedStyle, selectedRatio, setQueryValue, source])

  const handleSubmit = useCallback(() => {
    const openPremiumModal = (source: string, type: string, callback: () => void, video: string, placeholderImage: string) => {
      customAmplitude('Premium Prompt', { Source: source, Type: type })
      dispatch(setOpenModalTryPremium({
        isOpen: true,
        opened: true,
        source,
        callback,
        video,
        placeholderImage
      }))
    }

    const onSubmitAndValidation = () => {
      if (confirmQueryValue()) {  
        onSubmit?.()
      }
    }

    if (!hasPremium && premiumModalSource && premiumVideo && premiumPlaceholder) {
      openPremiumModal(
        premiumModalSource,
        'Standard',
        () => onSubmitAndValidation(),
        premiumVideo,
        premiumPlaceholder
      )
      return
    }
    onSubmitAndValidation()
  }, [onSubmit, hasPremium, dispatch, confirmQueryValue, premiumModalSource, premiumVideo, premiumPlaceholder])

  const handleEnterKeyDown = useCallback((e) => {
    if(e.key === 'Enter') {
      e.preventDefault()
      if(!hasConnection || isLoadingSuggestImages) return
      handleSubmit()
    }
  }, [hasConnection, handleSubmit, isLoadingSuggestImages])

  const handleCancelSubmit = useCallback(() => {
    onCancelSubmit?.()
  }, [onCancelSubmit])

  const [isTextareaActive, setIsTextareaActive] = useState(false)

  // Check if prompt contains newlines
  const hasNewline = prompt.includes('\n')

  const hasThumbnail = Boolean(actionsConfig.showPreviewImage && (thumbnailSrc || activeObject))

  // For "oneline" mainStyle, collapse is not relevant - always show one layout
  const isOneLine = mainStyle === 'oneline'

  if (isOneLine) {
    return (
      <TextBoxWrapperOneLine
        wrapperRef={wrapperRef}
        actionsConfig={actionsConfig}
        disabled={disabled}
        hasGradientBorder={hasGradientBorder}
        isActive={isTextareaActive || isLoadingSuggestImages}
        hasThumbnail={hasThumbnail}
        showErrorState={showErrorState}
      >
        {/* Flex container for preview image and text input */}
        <div 
          style={{  
            display: 'flex', 
            flexDirection: hasNewline ? 'column' : 'row',
            flexWrap: 'wrap',
            gap: '8px',
            flex: '1 1 0',
            minWidth: 0,
            alignItems: hasNewline ? 'stretch' : 'flex-start'
          }}
        >
          {/* Preview image container */}
          {actionsConfig.showPreviewImage && (
            <div style={{ flexShrink: 0, height: '40px', alignSelf: 'flex-start' }}>
              {thumbnailSrc ? (
                // Fixed height, width from the image's own ratio — so the preview
                // reads as the same frame the source is in. A 40px floor used to
                // square off anything portrait, and only very wide sources are
                // still cropped, by the max width.
                //
                // Same two-element construction as ImagePreview below, and for the
                // same reason: the hairline outline is an INSET shadow, which paints
                // behind a replaced element's content. So the visible pixels are the
                // div's background, and the img is a transparent sizer that lends the
                // box its intrinsic width.
                <div
                  style={{
                    backgroundImage: `url(${thumbnailSrc})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    maxWidth: '72px',
                    width: 'auto',
                    height: '100%',
                    boxShadow: `inset 0 0 0 1px ${hexToRgba(lightTheme.colors.black, 0.1)}`,
                    borderRadius: '8px',
                  }}
                >
                  <img
                    src={thumbnailSrc}
                    alt=""
                    draggable={false}
                    style={{ display: 'block', height: '40px', width: 'auto', borderRadius: '8px', objectFit: 'cover', opacity: 0 }}
                  />
                </div>
              ) : (
                <ImagePreview
                  activeObject={activeObject}
                  showCloseOnHover={false}
                  onCloseClick={() => {}}
                  editor={editor}
                  currentSceneId={currentSceneId}
                  disabled={disabled}
                  isOverLayer={showErrorState}
                />
              )}
            </div>
          )}
          
          {/* Text input - takes all available space, full width when wrapped. Empty, it
              only takes the row's leftover space: sized to its content it would measure
              the placeholder, and on a narrow box drop under the thumbnail before any
              text is typed. */}
          {!showErrorState && <div style={{ 
            flex: hasNewline ? '1 1 100%' : prompt ? '1 1 auto' : '1 1 0',
            minWidth: hasNewline ? '100%' : '200px',
            position: 'relative',
            alignSelf: 'center',
            marginRight: '2px'
          }}>
            <DynamicTextarea
              prompt={prompt}
              setPrompt={setPrompt}
              wrapScrollRef={wrapperRef}
              isCollapsed={false}
              setIsCollapsed={setIsCollapsed}
              handleKeyDown={handleEnterKeyDown}
              onUndoRedoShortcut={onUndoRedoShortcut}
              placeholder={placeholder}
              disabled={disabled}
              autoFocus={autoFocus}
              onFocus={() => {
                setIsTextareaActive(true)
                onFocus?.()
              }}
              onBlur={() => {
                setIsTextareaActive(false)
                onBlur?.()
              }}
              showClearTextButton={showClearTextButton}
              textareaId={textareaId}
              padding={[0, compact ? 0 : 48, 0, 0]}
              scrollbarTrackMarginTop={-49}
              // ref={textareaRef}
            />
          </div>}
        </div>
        
        {/* Submit button */}
        <div style={{ flexShrink: 0, alignSelf: showErrorState ? 'center' : 'flex-end', padding: '4px 0', position: 'absolute', right: '8px' }}>
          <SubmitButton
            isLoadingSuggestImages={isLoadingSuggestImages}
            isDefaultValue={isDefaultValue}
            prompt={prompt}
            hasPremium={hasPremium}
            showPremiumBadge={showPremiumBadge}
            hasConnection={hasConnection}
            handleSubmit={handleSubmit}
            handleCancelSubmit={handleCancelSubmit}
          />
        </div>
      </TextBoxWrapperOneLine>
    )
  }

  // Original layout for with actions mode
  return (
    <TextBoxWrapper
      wrapperRef={wrapperRef}
      isCollapsed={isCollapsed}
      actionsConfig={actionsConfig}
      disabled={disabled}
    >
      <DynamicTextarea
        prompt={prompt}
        setPrompt={setPrompt}
        wrapScrollRef={wrapScrollRef}
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        handleKeyDown={handleEnterKeyDown}
        onUndoRedoShortcut={onUndoRedoShortcut}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        onFocus={onFocus}
        onBlur={onBlur}
        showClearTextButton={showClearTextButton}
        textareaId={textareaId}
      />
      {!isCollapsed ? (
        <ActionButtons
          actionsConfig={actionsConfig}
          selectedStyle={selectedStyle}
          setSelectedStyle={setSelectedStyle}
          selectedRatio={selectedRatio}
          setSelectedRatio={setSelectedRatio}
          subMenuIsOpen={subMenuIsOpen}
          setSubMenuIsOpen={setSubMenuIsOpen}
          disabled={disabled}
          isLoadingSuggestImages={isLoadingSuggestImages}
          isDefaultValue={isDefaultValue}
          prompt={prompt}
          hasPremium={hasPremium}
          showPremiumBadge={showPremiumBadge}
          hasConnection={hasConnection}
          handleSubmit={handleSubmit}
          handleCancelSubmit={handleCancelSubmit}
          activeObject={activeObject}
          editor={editor}
          currentSceneId={currentSceneId}
          onStartLoading={onStartLoading}
          onStopLoading={onStopLoading}
          onSuccess={onSuccess}
        />
      ) : null}
    </TextBoxWrapper>
  )
}
